import { Fragment, useState, useRef, useEffect, useMemo, useCallback, type JSX, type ReactNode } from 'react'
import { useRecentlyUpdated, type RowMark } from './useRecentlyUpdated.js'
import Tooltip from '../ui/Tooltip.js'
import {
  Star, Archive, ArchiveRestore, Mail, MailOpen, MoreVertical, ExternalLink,
  Network, GitBranch, GitMerge, Sparkles, ChevronDown, ChevronRight, Loader2,
  Hash, User, Users, Clock, AlignLeft, Tag, Activity, Flag, AlertTriangle,
  Layers, FolderTree, MapPin, Building2, Building, Megaphone, Headset,
  Timer, TimerReset, Calendar, CalendarClock, PenLine, Milestone,
  Check, Minus, CheckCircle2, XCircle, Reply,
  type LucideIcon,
} from 'lucide-react'
import { formatDateTime, timestampMs } from '../../utils/timezone.js'
import { api } from '../../api/client.js'
import { sourceMeta } from './channelMeta.js'
import type { ConversationItem } from './ConversationInbox.js'
import { useConversationStatuses } from '../../hooks/useConversationStatuses.js'
import { presentConversationStatus, statusStyles } from '../../modules/conversations/statusCatalog.js'
import { toItem, type ApiConversation } from '../../hooks/useInboxConversations.js'

/** Radius cap for the whole inbox surface. */
const R = 'rounded-[3px]'

/** Per-user column-width overrides (drag-to-resize), keyed by column key. */
const COLUMN_WIDTH_PREF_KEY = 'kc3_inbox_column_widths'
const MIN_COLUMN_WIDTH = 60

/** A parent row's expanded child tickets — full inbox rows, so they render
 *  every column exactly like a top-level conversation. */
interface InboxChildState {
  items?: ConversationItem[]
  loading: boolean
  error?: string
}

/* ── Column model ─────────────────────────────────────────────────────────
   One definition per column drives the header, the body cell and the column
   picker, so the three can never drift apart. `stickyLeft` pins a column to
   the left edge at that offset — identity stays readable while the other ~20
   columns scroll horizontally.                                             */
export interface ColumnDef {
  key: string
  label: string
  width: number
  stickyLeft?: number
  align?: 'left' | 'center'
  /** Header glyph — a per-column icon so the column is recognisable at a glance. */
  icon?: LucideIcon
  /** Columns the user may not hide — without identity a row is unusable. */
  locked?: boolean
}

export const COLUMNS: ColumnDef[] = [
  // Bulk-action / reassignment select column (from the inbox-bulk-actions
  // feature) sits first; star + id follow, all pinned left. The rest carry a
  // per-column header icon. Source stays folded into the ID cell as a glyph.
  { key: 'select', label: '', width: 36, stickyLeft: 0, align: 'center', locked: true },
  { key: 'star', label: '', width: 40, stickyLeft: 36, align: 'center', locked: true },
  { key: 'id', label: 'Conversation ID', width: 324, stickyLeft: 76, locked: true, icon: Hash },
  { key: 'customer', label: 'Client', width: 250, icon: User },
  { key: 'age', label: 'Ageing', width: 200, icon: Clock },
  { key: 'subject', label: 'Subject', width: 350, icon: AlignLeft },
  { key: 'type', label: 'Type', width: 200, icon: Tag },
  { key: 'status', label: 'Status', width: 200, icon: Activity },
  { key: 'state', label: 'State', width: 200, icon: Milestone },
  { key: 'priority', label: 'Priority', width: 200, icon: Flag },
  { key: 'urgency', label: 'Urgency', width: 200, icon: AlertTriangle },
  { key: 'category', label: 'Category', width: 200, icon: Layers },
  { key: 'subcategory', label: 'Sub Category', width: 200, icon: FolderTree },
  { key: 'location', label: 'Location', width: 200, icon: MapPin },
  { key: 'branch', label: 'Branch', width: 200, icon: Building2 },
  { key: 'department', label: 'Department', width: 200, icon: Building },
  { key: 'campaign', label: 'Campaign', width: 200, icon: Megaphone },
  { key: 'team', label: 'Team', width: 200, icon: Users },
  { key: 'agent', label: 'Agent', width: 200, icon: Headset },
  { key: 'slaResponse', label: 'SLA Response', width: 140, icon: Timer },
  { key: 'slaResolution', label: 'SLA Resolution', width: 210, icon: TimerReset },
  { key: 'createdAt', label: 'Date Created', width: 210, icon: Calendar },
  { key: 'modifiedAt', label: 'Date Modified', width: 230, icon: CalendarClock },
  { key: 'modifiedBy', label: 'Last Modified By', width: 230, icon: PenLine },
  { key: 'more', label: 'Actions', width: 80, align: 'center', locked: true },
]

export const DEFAULT_VISIBLE_COLUMNS: string[] = COLUMNS.map((c) => c.key)

/* ── Cell primitives ── */

/** An unset value reads as a dash: the eye skips it, where a word like
 *  "Unassigned" repeated down twenty columns is pure noise. */
function Blank(): JSX.Element {
  return <span className="text-slate-300">—</span>
}

/** Clean custom checkbox — a rounded box that fills brand-blue with a crisp
 *  tick when checked (Minus when indeterminate), with hover + keyboard support.
 *  Replaces the native input so the selection UI reads as designed, not default. */
function Checkbox({
  checked, indeterminate = false, onToggle, ariaLabel,
}: {
  checked: boolean
  indeterminate?: boolean
  onToggle: () => void
  ariaLabel: string
}): JSX.Element {
  const on = checked || indeterminate
  return (
    <span
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      aria-label={ariaLabel}
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); onToggle() }}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); onToggle() }
      }}
      className={`inline-flex h-[17px] w-[17px] shrink-0 cursor-pointer items-center justify-center rounded-[5px] border transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3366FF]/30 ${
        on
          ? 'border-[#3366FF] bg-[#3366FF] text-white shadow-sm shadow-[#3366FF]/25'
          : 'border-slate-300 bg-white hover:border-[#3366FF] hover:bg-[#F5F8FF]'
      }`}
    >
      {indeterminate
        ? <Minus className="h-3 w-3" strokeWidth={3.5} />
        : checked ? <Check className="h-3 w-3" strokeWidth={3.5} /> : null}
    </span>
  )
}

/** Text preceded by a small muted glyph — used for the routing/org and date
 *  columns so each value carries a recognisable icon. Falls back to a dash
 *  (no icon) when unset, matching Text. */
function IconText({
  icon: Icon, value, className = '',
}: { icon: LucideIcon; value: string | null | undefined; className?: string }): JSX.Element {
  if (!value) return <Blank />
  return (
    <span className="flex min-w-0 items-center gap-1.5" title={value}>
      <Icon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
      <span className={`truncate ${className}`}>{value}</span>
    </span>
  )
}

type Tone = 'gray' | 'emerald' | 'amber' | 'red' | 'blue' | 'violet' | 'indigo'

const TONES: Record<Tone, string> = {
  gray: 'bg-slate-100 text-slate-700',
  emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  amber: 'bg-amber-50 text-amber-700 ring-amber-100',
  red: 'bg-red-50 text-red-700 ring-red-100',
  blue: 'bg-blue-50 text-blue-700 ring-blue-100',
  violet: 'bg-violet-50 text-violet-700 ring-violet-100',
  indigo: 'bg-indigo-50 text-indigo-700 ring-indigo-100',
}

function Pill({ tone = 'gray', dot, children }: { tone?: Tone; dot?: string; children: ReactNode }): JSX.Element {
  return (
    <span className={`inline-flex max-w-full items-center gap-1.5 ${R} ${TONES[tone]} px-2 py-1 text-[12px] font-semibold leading-none`}>
      {dot && <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} />}
      <span className="truncate">{children}</span>
    </span>
  )
}

/** Status pill for the inbox. Label and colour come from the row's linked
 *  `conversation_status` entry — the catalog is the only source, so a status
 *  renamed or recoloured in admin shows through here immediately. */
function StatusCell({ conversation }: { conversation: ConversationItem }): JSX.Element {
  const { statuses } = useConversationStatuses()
  const present = presentConversationStatus(statuses, {
    status: conversation.status,
    conversationStatusId: conversation.conversationStatusId ?? null,
    statusName: conversation.statusLabel ?? null,
    statusColor: conversation.statusColor ?? null,
  })
  const styles = statusStyles(present.color)
  return (
    <span
      className={`inline-flex max-w-full items-center gap-1.5 ${R} ${TONES.gray} px-2 py-1 text-[12px] font-semibold leading-none`}
      style={styles.pill}
    >
      <span
        className={`h-1.5 w-1.5 shrink-0 rounded-full ${present.color ? '' : 'bg-slate-400'}`}
        style={styles.dot}
      />
      <span className="truncate">{present.label}</span>
    </span>
  )
}

const PRIORITY_PILL: Record<string, { tone: Tone; label: string }> = {
  low: { tone: 'gray', label: 'Low' },
  medium: { tone: 'blue', label: 'Medium' },
  high: { tone: 'amber', label: 'High' },
  urgent: { tone: 'red', label: 'Urgent' },
}

/** Urgency labels are tenant-defined free text, so tone is inferred from
 *  keywords and falls back to neutral rather than guessing a severity. */
function urgencyTone(label: string): Tone {
  const l = label.toLowerCase()
  if (/critical|severe|high risk|urgent/.test(l)) return 'red'
  if (/high|medium risk/.test(l)) return 'amber'
  if (/low|minor|no risk/.test(l)) return 'emerald'
  return 'gray'
}

function relativeFromNow(iso: string): string {
  const diffMs = timestampMs(iso) - Date.now()
  const abs = Math.abs(diffMs)
  const mins = Math.floor(abs / 60000)
  const hours = Math.floor(abs / 3600000)
  const days = Math.floor(abs / 86400000)
  const span = mins < 60 ? `${Math.max(mins, 1)}m` : hours < 24 ? `${hours}h` : `${days}d`
  return diffMs >= 0 ? `in ${span}` : `${span} late`
}

function timeAgo(iso: string): string {
  const diffMs = Date.now() - timestampMs(iso)
  const mins = Math.floor(diffMs / 60000)
  const hours = Math.floor(diffMs / 3600000)
  const days = Math.floor(diffMs / 86400000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`
  return formatDateTime(iso)
}

/** SLA clock: overdue is red, due within the hour amber, otherwise muted. The
 *  absolute timestamp stays on hover so the relative figure is never the only
 *  thing to go on. When the SLA has been met (completedAt exists), shows "Met"
 *  in green instead of a countdown. */
function SlaCell({ due, completedAt, icon: Icon }: { due: string | undefined; completedAt?: string | undefined; icon: LucideIcon }): JSX.Element {
  if (!due) return <Blank />
  if (completedAt) {
    const onTime = timestampMs(completedAt) <= timestampMs(due)
    if (onTime) {
      return (
        <span className="flex min-w-0 items-center gap-1.5 font-semibold text-emerald-600" title={`Met on ${formatDateTime(completedAt)}`}>
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">Met</span>
        </span>
      )
    }
    return (
      <span className="flex min-w-0 items-center gap-1.5 font-semibold text-red-600" title={`Breached — responded at ${formatDateTime(completedAt)}`}>
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">Breached</span>
      </span>
    )
  }
  const remainingMs = timestampMs(due) - Date.now()
  const cls = remainingMs < 0
    ? 'text-red-600 font-semibold'
    : remainingMs < 3600000
      ? 'text-amber-600 font-semibold'
      : 'text-slate-500'
  return (
    <span className={`flex min-w-0 items-center gap-1.5 tabular-nums ${cls}`} title={formatDateTime(due)}>
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate">{relativeFromNow(due)}</span>
    </span>
  )
}

/** Icon-only source glyph — the transport a conversation arrived on, shown as a
 *  tinted brand icon (no label). Merged into the Conversation ID cell so the
 *  source is visible at a glance without a dedicated column. */
function SourceGlyph({ source, channel }: { source: string | undefined; channel: string }): JSX.Element {
  const meta = sourceMeta(source, channel)
  const Icon = meta.icon
  return (
    <span className={`flex h-6 w-6 shrink-0 items-center justify-center ${R} ${meta.bg} ${meta.fg}`} title={meta.label} aria-label={meta.label}>
      <Icon className="h-3.5 w-3.5" />
    </span>
  )
}

const AVATAR_COLORS = [
  'bg-rose-100 text-rose-700', 'bg-amber-100 text-amber-700', 'bg-emerald-100 text-emerald-700',
  'bg-sky-100 text-sky-700', 'bg-violet-100 text-violet-700', 'bg-orange-100 text-orange-700',
  'bg-teal-100 text-teal-700', 'bg-indigo-100 text-indigo-700',
]

function avatarColor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]!
}

/** Initials that read sensibly for real names, bare emails and @handles alike. */
function initials(name: string): string {
  const t = name.trim()
  if (!t) return '?'
  if (t.startsWith('@')) {
    const handle = t.slice(1)
    const parts = handle.split(/(?=[A-Z])/).filter(Boolean)
    if (parts.length >= 2) return parts.slice(0, 2).map((p) => p[0]!.toUpperCase()).join('')
    return handle.slice(0, 2).toUpperCase() || '?'
  }
  if (t.includes('@') && !t.includes(' ')) return (t.split('@')[0] ?? t).slice(0, 2).toUpperCase()
  return t.split(/\s+/).filter(Boolean).map((n) => n[0]).join('').toUpperCase().slice(0, 2)
}

/* ── Linked conversations ───────────────────────────────────────────────────────
   A conversation can be tied to another in two independent ways — parent/child and
   merge — and the row used to signal both with an unlabelled glyph: you could
   see that a conversation was linked, but not to *what*. Each relationship renders
   as "<relation> #REFERENCE" so both ends are named, and the reference is
   clickable wherever the linked conversation's id is known, so the link can
   actually be followed. */

interface LinkedRelation {
  key: string
  /** How this row relates to the conversations in `refs` — reads left to right. */
  label: string
  /** One-word badge text; `label` carries the full sentence in the tooltip. */
  short: string
  icon: LucideIcon
  tone: 'violet' | 'indigo' | 'gray'
  /** Reference ids of the linked conversations. Capped by the API for the
   *  one-to-many relations; `total` stays exact. */
  refs: string[]
  total: number
  /** Only set for the one-to-one relations, where a single conversation can be
   *  opened directly from the chip. */
  openId?: string | undefined
}

const LINK_TONES: Record<LinkedRelation['tone'], string> = {
  violet: 'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-100',
  indigo: 'bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-100',
  gray: 'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200',
}

/** Every link this row participates in. `childCount`/`secondaryCount` are the
 *  authority for the one-to-many sides — `isParentTicket` can stay set on a
 *  conversation whose children were all reparented, which would otherwise render a
 *  "Parent of" chip naming nothing. */
function linkedRelations(c: ConversationItem): LinkedRelation[] {
  const relations: LinkedRelation[] = []

  if (c.parentReferenceId) {
    relations.push({
      key: 'parent', label: 'Child of', short: 'Child', icon: GitBranch, tone: 'violet',
      refs: [c.parentReferenceId], total: 1, openId: c.parentId,
    })
  }

  const childRefs = c.childReferenceIds ?? []
  const childTotal = c.childCount ?? 0
  if (childTotal > 0) {
    relations.push({
      key: 'children', label: childTotal === 1 ? 'Parent of' : `Parent of ${childTotal}`,
      short: 'Parent', icon: Network, tone: 'indigo', refs: childRefs, total: childTotal,
    })
  }

  if (c.mergedIntoReferenceId) {
    relations.push({
      key: 'merged-into', label: 'Secondary — merged into', short: 'Secondary',
      icon: GitMerge, tone: 'gray',
      refs: [c.mergedIntoReferenceId], total: 1, openId: c.mergedIntoId,
    })
  }

  const secondaryRefs = c.secondaryReferenceIds ?? []
  const secondaryTotal = c.secondaryCount ?? 0
  if (secondaryTotal > 0) {
    relations.push({
      key: 'secondaries',
      label: secondaryTotal === 1 ? 'Primary — merged in' : `Primary — ${secondaryTotal} merged in`,
      short: 'Primary', icon: GitMerge, tone: 'violet', refs: secondaryRefs, total: secondaryTotal,
    })
  }

  return relations
}

/**
 * Compact relation markers rendered inside the Conversation ID cell.
 *
 * These replace the old wide "Linked Conversations" column: the relationships are a
 * property of the conversation's identity, so they belong beside its reference
 * rather than in a column of their own. Each badge names both ends on hover —
 * a marker that only says "this is linked" leaves you no better off.
 */
function RelationBadges({ row, onOpen }: { row: ConversationItem; onOpen: (id: string) => void }): JSX.Element | null {
  const relations = linkedRelations(row)
  if (relations.length === 0) return null

  return (
    <>
      {relations.map((relation) => {
        const Icon = relation.icon
        const named = relation.refs.map((x) => x).join(', ')
        const hidden = relation.total - relation.refs.length
        const tip = named
          ? `${relation.label} ${named}${hidden > 0 ? ` and ${hidden} more` : ''}`
          : relation.label
        const body = (
          <span
            className={`inline-flex shrink-0 items-center gap-0.5 ${R} ${LINK_TONES[relation.tone]} px-1 py-0.5 text-[9px] font-bold uppercase tracking-wide`}
          >
            <Icon className="h-2.5 w-2.5" />
            {relation.short}
            {relation.total > 1 && <span className="tabular-nums">{relation.total}</span>}
          </span>
        )
        return (
          <Tooltip key={relation.key} content={tip} className="inline-flex shrink-0">
            {relation.openId ? (
              // The row's own onClick opens *this* conversation, so following a
              // link has to stop propagation or both would fire.
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onOpen(relation.openId!) }}
                className="inline-flex cursor-pointer"
                aria-label={tip}
              >
                {body}
              </button>
            ) : (
              body
            )}
          </Tooltip>
        )
      })}
    </>
  )
}

function CustomerCell({ row }: { row: ConversationItem }): JSX.Element {
  return (
    <span className="flex items-center gap-2.5" title={row.customerHandle ?? row.customerName}>
      {row.customerAvatar
        ? <img src={row.customerAvatar} alt="" className={`h-7 w-7 shrink-0 ${R} object-cover`} />
        : <span className={`flex h-7 w-7 shrink-0 items-center justify-center ${R} text-[11px] font-bold ${avatarColor(row.customerName)}`}>{initials(row.customerName)}</span>}
      <span className={`truncate ${row.isRead ? 'text-slate-700' : 'font-semibold text-[#0f172a]'}`}>{row.customerName}</span>
    </span>
  )
}

/* ── Row action menu ──────────────────────────────────────────────────────
   Positioned fixed from the button's own rect: a menu inside the
   horizontally-scrolling table body would be clipped by the scroll container. */
interface RowActions {
  onOpen: () => void
  onToggleStar: () => void
  onToggleRead: () => void
  onToggleArchive: () => void
  isRead: boolean
  isStarred: boolean
  isArchived: boolean
}

function MoreMenu({ actions }: { actions: RowActions }): JSX.Element {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    // Any scroll or resize invalidates the captured rect — close rather than
    // let the menu drift away from the row it belongs to.
    const closeOnViewportChange = (): void => setOpen(false)
    // mousedown (not click) fires before a menu item's own onClick — a naive
    // "any mousedown closes the menu" handler was closing it out from under
    // the click before the item's action ever ran. Only close for mousedowns
    // outside both the trigger button and the menu itself.
    const closeOnOutsideClick = (e: MouseEvent): void => {
      const t = e.target as Node
      if (btnRef.current?.contains(t)) return
      if (menuRef.current?.contains(t)) return
      setOpen(false)
    }
    window.addEventListener('scroll', closeOnViewportChange, true)
    window.addEventListener('resize', closeOnViewportChange)
    document.addEventListener('mousedown', closeOnOutsideClick)
    return () => {
      window.removeEventListener('scroll', closeOnViewportChange, true)
      window.removeEventListener('resize', closeOnViewportChange)
      document.removeEventListener('mousedown', closeOnOutsideClick)
    }
  }, [open])

  const items: { label: string; icon: JSX.Element; run: () => void }[] = [
    { label: 'Open conversation', icon: <ExternalLink className="h-4 w-4" />, run: actions.onOpen },
    { label: actions.isStarred ? 'Remove star' : 'Add star', icon: <Star className="h-4 w-4" />, run: actions.onToggleStar },
    { label: actions.isRead ? 'Mark unread' : 'Mark read', icon: actions.isRead ? <Mail className="h-4 w-4" /> : <MailOpen className="h-4 w-4" />, run: actions.onToggleRead },
    { label: actions.isArchived ? 'Unarchive' : 'Archive', icon: actions.isArchived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />, run: actions.onToggleArchive },
  ]

  return (
    <>
      <button
        ref={btnRef}
        onClick={(e) => {
          e.stopPropagation()
          const rect = btnRef.current?.getBoundingClientRect()
          if (rect) setPos({ top: rect.bottom + 6, left: Math.max(8, rect.right - 200) })
          setOpen((v) => !v)
        }}
        className={`flex h-7 w-7 items-center justify-center ${R} text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700`}
        title="More actions"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {open && pos && (
        <div
          ref={menuRef}
          className={`fixed z-50 w-52 overflow-hidden ${R} border border-slate-200 bg-white py-1 shadow-xl shadow-black/10`}
          style={{ top: pos.top, left: pos.left }}
          onClick={(e) => e.stopPropagation()}
        >
          {items.map((it) => (
            <button
              key={it.label}
              onClick={() => { it.run(); setOpen(false) }}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900"
            >
              <span className="text-slate-400">{it.icon}</span>{it.label}
            </button>
          ))}
        </div>
      )}
    </>
  )
}

/** Excel-style drag handle on a header cell's right edge. Uses document-level
 *  mousemove/mouseup (not Pointer Events/setPointerCapture) so dragging keeps
 *  working even if the cursor leaves the thin handle strip mid-drag — the
 *  same robust pattern as the outside-click handlers elsewhere in this
 *  codebase. `isResizing` drives a highlighted state while active so it's
 *  obvious the drag registered. */
function ResizeHandle({
  startWidth, onResize, onResizeEnd,
}: {
  startWidth: number
  onResize: (width: number) => void
  onResizeEnd: (width: number) => void
}): JSX.Element {
  const [isResizing, setIsResizing] = useState(false)

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    const startX = e.clientX
    let currentWidth = startWidth
    setIsResizing(true)

    const handleMouseMove = (moveEvent: MouseEvent): void => {
      currentWidth = Math.max(MIN_COLUMN_WIDTH, Math.round(startWidth + (moveEvent.clientX - startX)))
      onResize(currentWidth)
    }
    const handleMouseUp = (): void => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      setIsResizing(false)
      onResizeEnd(currentWidth)
    }
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  return (
    <div
      onMouseDown={handleMouseDown}
      onClick={(e) => e.stopPropagation()}
      className="absolute right-0 top-0 z-40 flex h-full w-2.5 cursor-col-resize select-none items-center justify-end"
      title="Drag to resize column"
    >
      <div className={`h-1/2 transition-colors ${isResizing ? 'w-[2px] bg-[#2952CC]' : 'w-px bg-slate-300 hover:w-[2px] hover:bg-[#3366FF]'}`} />
    </div>
  )
}

/* ── Table ── */
interface ConversationTableProps {
  rows: ConversationItem[]
  visibleColumns: string[]
  selectedIds: Set<string>
  onToggleSelect: (id: string) => void
  onToggleSelectAll: () => void
  onOpen: (id: string) => void
  onToggleStar: (id: string) => void
  onMarkRead: (ids: string[]) => void
  onMarkUnread: (ids: string[]) => void
  onArchive: (ids: string[]) => void
  onUnarchive: (ids: string[]) => void
  /** When false, the row-select checkbox column is hidden entirely — used when
   *  the user has no bulk-action / reassignment permission, so there's nothing
   *  to select for. */
  selectable?: boolean
  /** Identifies the list being shown (scope/filter/search/page). A change means
   *  a different list, not new activity, so the "New" highlight re-baselines
   *  instead of flagging every row on the incoming page. */
  highlightKey?: string
}

export default function ConversationTable({
  rows, visibleColumns, selectedIds, onToggleSelect, onToggleSelectAll,
  onOpen, onToggleStar, onMarkRead, onMarkUnread, onArchive, onUnarchive,
  selectable = true, highlightKey = '',
}: ConversationTableProps): JSX.Element {
  const [expandedParents, setExpandedParents] = useState<Set<string>>(() => new Set())
  const [childrenByParent, setChildrenByParent] = useState<Record<string, InboxChildState>>({})
  const childLoadsInFlight = useRef(new Set<string>())

  // Merge-relationship expand/collapse — mirrors the parent/child tree but
  // for primary↔secondary (merged) tickets.
  const [expandedPrimaries, setExpandedPrimaries] = useState<Set<string>>(() => new Set())
  const [secondariesByPrimary, setSecondariesByPrimary] = useState<Record<string, InboxChildState>>({})
  const secondaryLoadsInFlight = useRef(new Set<string>())
  const [expandedSecondaries, setExpandedSecondaries] = useState<Set<string>>(() => new Set())
  const [primariesBySecondary, setPrimariesBySecondary] = useState<Record<string, InboxChildState>>({})
  const primaryLoadsInFlight = useRef(new Set<string>())

  const loadChildren = useCallback((parentId: string): void => {
    if (childLoadsInFlight.current.has(parentId)) return
    childLoadsInFlight.current.add(parentId)
    setChildrenByParent((prev) => ({
      ...prev,
      [parentId]: {
        ...(prev[parentId]?.items !== undefined ? { items: prev[parentId].items } : {}),
        loading: true,
      },
    }))

    void (async () => {
      try {
        const all: ApiConversation[] = []
        // The inbox's active filters can omit archived children. Fetch both
        // sets so the parent row can show every direct child conversation.
        for (const filter of ['all', 'archived']) {
          let offset = 0
          do {
            const params = new URLSearchParams({ parentId, filter, limit: '200', offset: String(offset) })
            const response = await api.get<{ items: ApiConversation[]; nextOffset: number | null }>(
              `/conversations?${params.toString()}`,
            )
            all.push(...(response.items ?? []))
            offset = response.nextOffset ?? -1
          } while (offset >= 0)
        }
        const uniqueById = new Map<string, ConversationItem>()
        for (const child of all) uniqueById.set(child.id, toItem(child))
        setChildrenByParent((prev) => ({
          ...prev,
          [parentId]: { items: [...uniqueById.values()], loading: false },
        }))
      } catch (error) {
        setChildrenByParent((prev) => ({
          ...prev,
          [parentId]: {
            ...(prev[parentId] ?? {}),
            loading: false,
            error: error instanceof Error ? error.message : 'Unable to load child tickets',
          },
        }))
      } finally {
        childLoadsInFlight.current.delete(parentId)
      }
    })()
  }, [])

  const toggleChildren = useCallback((parentId: string): void => {
    const isExpanded = expandedParents.has(parentId)
    setExpandedParents((prev) => {
      const next = new Set(prev)
      if (next.has(parentId)) next.delete(parentId)
      else next.add(parentId)
      return next
    })
    if (!isExpanded && !childrenByParent[parentId]?.items && !childrenByParent[parentId]?.loading) {
      loadChildren(parentId)
    }
  }, [childrenByParent, expandedParents, loadChildren])

  // Load secondaries merged INTO a primary ticket.
  const loadSecondaries = useCallback((primaryId: string): void => {
    if (secondaryLoadsInFlight.current.has(primaryId)) return
    secondaryLoadsInFlight.current.add(primaryId)
    setSecondariesByPrimary((prev) => ({
      ...prev,
      [primaryId]: {
        ...(prev[primaryId]?.items !== undefined ? { items: prev[primaryId].items } : {}),
        loading: true,
      },
    }))

    void (async () => {
      try {
        const all: ApiConversation[] = []
        for (const filter of ['all', 'archived']) {
          let offset = 0
          do {
            const params = new URLSearchParams({ mergedIntoId: primaryId, filter, limit: '200', offset: String(offset) })
            const response = await api.get<{ items: ApiConversation[]; nextOffset: number | null }>(
              `/conversations?${params.toString()}`,
            )
            all.push(...(response.items ?? []))
            offset = response.nextOffset ?? -1
          } while (offset >= 0)
        }
        const uniqueById = new Map<string, ConversationItem>()
        for (const sec of all) uniqueById.set(sec.id, toItem(sec))
        setSecondariesByPrimary((prev) => ({
          ...prev,
          [primaryId]: { items: [...uniqueById.values()], loading: false },
        }))
      } catch (error) {
        setSecondariesByPrimary((prev) => ({
          ...prev,
          [primaryId]: {
            ...(prev[primaryId] ?? {}),
            loading: false,
            error: error instanceof Error ? error.message : 'Unable to load secondary tickets',
          },
        }))
      } finally {
        secondaryLoadsInFlight.current.delete(primaryId)
      }
    })()
  }, [])

  const toggleSecondaries = useCallback((primaryId: string): void => {
    const isExpanded = expandedPrimaries.has(primaryId)
    setExpandedPrimaries((prev) => {
      const next = new Set(prev)
      if (next.has(primaryId)) next.delete(primaryId)
      else next.add(primaryId)
      return next
    })
    if (!isExpanded && !secondariesByPrimary[primaryId]?.items && !secondariesByPrimary[primaryId]?.loading) {
      loadSecondaries(primaryId)
    }
  }, [secondariesByPrimary, expandedPrimaries, loadSecondaries])

  // Load the primary a secondary is merged into (just one conversation).
  const loadPrimary = useCallback((secondaryId: string, mergedIntoId: string): void => {
    if (primaryLoadsInFlight.current.has(secondaryId)) return
    primaryLoadsInFlight.current.add(secondaryId)
    setPrimariesBySecondary((prev) => ({
      ...prev,
      [secondaryId]: {
        ...(prev[secondaryId]?.items !== undefined ? { items: prev[secondaryId].items } : {}),
        loading: true,
      },
    }))

    void (async () => {
      try {
        const response = await api.get<ApiConversation>(`/conversations/${mergedIntoId}`)
        setPrimariesBySecondary((prev) => ({
          ...prev,
          [secondaryId]: { items: [toItem(response)], loading: false },
        }))
      } catch (error) {
        setPrimariesBySecondary((prev) => ({
          ...prev,
          [secondaryId]: {
            ...(prev[secondaryId] ?? {}),
            loading: false,
            error: error instanceof Error ? error.message : 'Unable to load primary ticket',
          },
        }))
      } finally {
        primaryLoadsInFlight.current.delete(secondaryId)
      }
    })()
  }, [])

  const togglePrimary = useCallback((secondaryId: string, mergedIntoId: string): void => {
    const isExpanded = expandedSecondaries.has(secondaryId)
    setExpandedSecondaries((prev) => {
      const next = new Set(prev)
      if (next.has(secondaryId)) next.delete(secondaryId)
      else next.add(secondaryId)
      return next
    })
    if (!isExpanded && !primariesBySecondary[secondaryId]?.items && !primariesBySecondary[secondaryId]?.loading) {
      loadPrimary(secondaryId, mergedIntoId)
    }
  }, [primariesBySecondary, expandedSecondaries, loadPrimary])

  /** Row actions on an expanded child update the inbox list, which doesn't
   *  hold that child — mirror the change onto the nested copy so it shows. */
  const patchChild = useCallback((id: string, patch: Partial<ConversationItem>): void => {
    setChildrenByParent((prev) => {
      let changed = false
      const next: Record<string, InboxChildState> = {}
      for (const [parentId, state] of Object.entries(prev)) {
        if (state.items?.some((c) => c.id === id)) {
          changed = true
          next[parentId] = { ...state, items: state.items.map((c) => (c.id === id ? { ...c, ...patch } : c)) }
        } else {
          next[parentId] = state
        }
      }
      return changed ? next : prev
    })
    // Also patch secondaries/primaries
    setSecondariesByPrimary((prev) => {
      let changed = false
      const next: Record<string, InboxChildState> = {}
      for (const [primaryId, state] of Object.entries(prev)) {
        if (state.items?.some((c) => c.id === id)) {
          changed = true
          next[primaryId] = { ...state, items: state.items.map((c) => (c.id === id ? { ...c, ...patch } : c)) }
        } else {
          next[primaryId] = state
        }
      }
      return changed ? next : prev
    })
    setPrimariesBySecondary((prev) => {
      let changed = false
      const next: Record<string, InboxChildState> = {}
      for (const [secondaryId, state] of Object.entries(prev)) {
        if (state.items?.some((c) => c.id === id)) {
          changed = true
          next[secondaryId] = { ...state, items: state.items.map((c) => (c.id === id ? { ...c, ...patch } : c)) }
        } else {
          next[secondaryId] = state
        }
      }
      return changed ? next : prev
    })
  }, [])

  const parentIdsOnPage = useMemo(() => new Set(rows.map((row) => row.id)), [rows])
  const nestedChildIds = useMemo(() => {
    const ids = new Set<string>()
    for (const [parentId, state] of Object.entries(childrenByParent)) {
      if (!expandedParents.has(parentId) || !parentIdsOnPage.has(parentId)) continue
      for (const child of state.items ?? []) ids.add(child.id)
    }
    return ids
  }, [childrenByParent, expandedParents, parentIdsOnPage])
  
  const nestedSecondaryIds = useMemo(() => {
    const ids = new Set<string>()
    for (const [primaryId, state] of Object.entries(secondariesByPrimary)) {
      if (!expandedPrimaries.has(primaryId) || !parentIdsOnPage.has(primaryId)) continue
      for (const sec of state.items ?? []) ids.add(sec.id)
    }
    return ids
  }, [secondariesByPrimary, expandedPrimaries, parentIdsOnPage])
  
  const nestedPrimaryIds = useMemo(() => {
    const ids = new Set<string>()
    for (const [secondaryId, state] of Object.entries(primariesBySecondary)) {
      if (!expandedSecondaries.has(secondaryId) || !parentIdsOnPage.has(secondaryId)) continue
      for (const prim of state.items ?? []) ids.add(prim.id)
    }
    return ids
  }, [primariesBySecondary, expandedSecondaries, parentIdsOnPage])
  
  const allNestedIds = useMemo(() => {
    return new Set([...nestedChildIds, ...nestedSecondaryIds, ...nestedPrimaryIds])
  }, [nestedChildIds, nestedSecondaryIds, nestedPrimaryIds])
  
  const visibleRows = useMemo(() => rows.filter((row) => !allNestedIds.has(row.id)), [rows, allNestedIds])

  // Rows that changed since the last poll, so a conversation bumped to the top by a
  // new reply is visibly called out rather than silently re-sorted.
  const { marks: recentMarks, clear: clearHighlight } = useRecentlyUpdated(rows, highlightKey)

  /** The highlight flags conversations the agent hasn't looked at yet, so a row
   *  they've already read never carries one — even if a background update bumped
   *  its timestamp. Opening a row marks it read, which retires the badge on the
   *  spot; `clearHighlight` covers the case where that write is still in flight. */
  const readState = useMemo(() => new Map(rows.map((r) => [r.id, r.isRead])), [rows])
  const markOf = (id: string): RowMark | undefined =>
    readState.get(id) === false ? recentMarks.get(id) : undefined

  const handleOpen = (id: string): void => {
    clearHighlight(id)
    patchChild(id, { isRead: true })
    onOpen(id)
  }

  // Column widths are a per-user preference (drag-to-resize), layered over
  // the COLUMNS defaults — same pattern as the visible-columns preference in
  // ConversationInbox.tsx, but scoped to this component since resizing is a
  // pure table-rendering concern.
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    try {
      const raw = localStorage.getItem(COLUMN_WIDTH_PREF_KEY)
      if (!raw) return {}
      const parsed: unknown = JSON.parse(raw)
      if (!parsed || typeof parsed !== 'object') return {}
      const result: Record<string, number> = {}
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof v === 'number' && v >= MIN_COLUMN_WIDTH) result[k] = v
      }
      return result
    } catch {
      return {}
    }
  })

  // Live drag updates state on every mousemove (cheap — no persistence) so
  // the column visibly tracks the cursor; the width is only written to
  // localStorage once, when the drag ends.
  const handleResize = (key: string, width: number): void => {
    setColumnWidths((prev) => ({ ...prev, [key]: width }))
  }
  const handleResizeEnd = (key: string, width: number): void => {
    setColumnWidths((prev) => {
      const next = { ...prev, [key]: width }
      try { localStorage.setItem(COLUMN_WIDTH_PREF_KEY, JSON.stringify(next)) } catch { /* preference only */ }
      return next
    })
  }

  // Render columns in the user's saved order. The sticky/locked columns keep
  // fixed positions (star + id pinned left, actions pinned right); every other
  // visible column follows the order the user arranged in the Columns menu.
  const byKey = new Map(COLUMNS.map((c) => [c.key, c]))
  const middleKeys = visibleColumns.filter((k) => {
    const c = byKey.get(k)
    return c !== undefined && !c.locked
  })
  const orderedKeys = [
    ...COLUMNS
      .filter((c) => c.locked && c.key !== 'more' && visibleColumns.includes(c.key))
      // Drop the select-checkbox column when the user can't act on a selection.
      .filter((c) => c.key !== 'select' || selectable)
      .map((c) => c.key),
    ...middleKeys,
    ...COLUMNS.filter((c) => c.key === 'more' && visibleColumns.includes(c.key)).map((c) => c.key),
  ]
  // Recompute the left offset of the pinned columns from the widths of the
  // columns actually rendered before them — otherwise hiding the select column
  // leaves its 36px slot as a gap (star/id keep their hardcoded stickyLeft).
  let stickyAcc = 0
  const cols = orderedKeys
    .map((k) => byKey.get(k))
    .filter((c): c is ColumnDef => c !== undefined)
    .map((c) => {
      const width = columnWidths[c.key] ?? c.width
      if (c.stickyLeft !== undefined) {
        const stickyLeft = stickyAcc
        stickyAcc += width
        return { ...c, width, stickyLeft }
      }
      return { ...c, width }
    })

  const childLoading = (parentId: string): boolean => childrenByParent[parentId]?.loading === true
  const secondaryLoading = (primaryId: string): boolean => secondariesByPrimary[primaryId]?.loading === true
  const primaryLoading = (secondaryId: string): boolean => primariesBySecondary[secondaryId]?.loading === true
  // Reserve the arrow slot on every row once any parent is on the page, so
  // reference ids stay in one column instead of jumping by the arrow's width.
  const hasParentRows = visibleRows.some((r) => (r.childCount ?? 0) > 0)

  const allOnPageSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id))
  const someOnPageSelected = rows.some((r) => selectedIds.has(r.id)) && !allOnPageSelected

  /** `nested` marks an expanded child row — 'last' ends the tree line. */
  const cellFor = (c: ConversationItem, key: string, nested?: 'mid' | 'last'): ReactNode => {
    switch (key) {
      case 'select':
        return (
          <Checkbox
            checked={selectedIds.has(c.id)}
            onToggle={() => onToggleSelect(c.id)}
            ariaLabel={`Select conversation ${c.interactionId ?? c.id}`}
          />
        )
      case 'star':
        return (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleStar(c.id); patchChild(c.id, { isStarred: !c.isStarred }) }}
            className={`flex h-7 w-7 items-center justify-center ${R} transition-colors ${c.isStarred ? 'text-amber-400' : 'text-slate-200 hover:bg-slate-100 hover:text-amber-400'}`}
            title={c.isStarred ? 'Remove star' : 'Add star'}
          >
            <Star className={`h-4 w-4 ${c.isStarred ? 'fill-amber-400' : ''}`} />
          </button>
        )
      case 'id': {
        const childTotal = c.childCount ?? 0
        const secondaryTotal = c.secondaryCount ?? 0
        const isPrimary = secondaryTotal > 0
        const isSecondary = Boolean(c.mergedIntoId)
        const expandedChild = expandedParents.has(c.id)
        const expandedSecondary = expandedPrimaries.has(c.id)
        const expandedPrimary = expandedSecondaries.has(c.id)
        
        return (
          <span className="flex items-center gap-1.5">
            {nested ? (
              // Tree elbow: the child hangs off the parent's arrow column.
              <span className="relative ml-2 h-[54px] w-4 shrink-0 self-stretch" aria-hidden>
                {nested === 'mid' && <span className="absolute bottom-0 left-0 top-1/2 border-l-2 border-violet-200" />}
                <span className="absolute left-0 top-0 h-1/2 w-3 rounded-bl-[4px] border-b-2 border-l-2 border-violet-200" />
              </span>
            ) : (
              <>
                {/* Child expand button */}
                {childTotal > 0 ? (
                  <button
                    type="button"
                    aria-expanded={expandedChild}
                    aria-label={`${expandedChild ? 'Hide' : 'Show'} ${childTotal} child tickets for ${c.interactionId ?? c.id}`}
                    title={`${expandedChild ? 'Hide' : 'Show'} ${childTotal} child ticket${childTotal === 1 ? '' : 's'}`}
                    onClick={(e) => { e.stopPropagation(); toggleChildren(c.id) }}
                    className={`flex h-6 w-6 shrink-0 items-center justify-center ${R} transition-colors ${
                      expandedChild ? 'bg-violet-100 text-violet-700' : 'text-violet-500 hover:bg-violet-50 hover:text-violet-700'
                    }`}
                  >
                    {childLoading(c.id)
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <ChevronRight className={`h-4 w-4 transition-transform duration-150 ${expandedChild ? 'rotate-90' : ''}`} />}
                  </button>
                ) : (hasParentRows || isPrimary || isSecondary) ? (
                  <span className="w-6 shrink-0" aria-hidden />
                ) : null}
                
                {/* Primary (has secondaries) expand button */}
                {isPrimary && (
                  <button
                    type="button"
                    aria-expanded={expandedSecondary}
                    aria-label={`${expandedSecondary ? 'Hide' : 'Show'} ${secondaryTotal} merged tickets for ${c.interactionId ?? c.id}`}
                    title={`${expandedSecondary ? 'Hide' : 'Show'} ${secondaryTotal} merged ticket${secondaryTotal === 1 ? '' : 's'}`}
                    onClick={(e) => { e.stopPropagation(); toggleSecondaries(c.id) }}
                    className={`flex h-6 w-6 shrink-0 items-center justify-center ${R} transition-colors ${
                      expandedSecondary ? 'bg-indigo-100 text-indigo-700' : 'text-indigo-500 hover:bg-indigo-50 hover:text-indigo-700'
                    }`}
                  >
                    {secondaryLoading(c.id)
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <ChevronRight className={`h-4 w-4 transition-transform duration-150 ${expandedSecondary ? 'rotate-90' : ''}`} />}
                  </button>
                )}
                
                {/* Secondary (merged into primary) expand button */}
                {isSecondary && (
                  <button
                    type="button"
                    aria-expanded={expandedPrimary}
                    aria-label={`${expandedPrimary ? 'Hide' : 'Show'} primary ticket for ${c.interactionId ?? c.id}`}
                    title={`${expandedPrimary ? 'Hide' : 'Show'} primary ticket`}
                    onClick={(e) => { e.stopPropagation(); togglePrimary(c.id, c.mergedIntoId!) }}
                    className={`flex h-6 w-6 shrink-0 items-center justify-center ${R} transition-colors ${
                      expandedPrimary ? 'bg-slate-200 text-slate-700' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                    }`}
                  >
                    {primaryLoading(c.id)
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <ChevronRight className={`h-4 w-4 transition-transform duration-150 ${expandedPrimary ? 'rotate-90' : ''}`} />}
                  </button>
                )}
              </>
            )}
            <SourceGlyph source={c.source} channel={c.channel} />
            {c.interactionId ? (
              <span
                className={`inline-flex max-w-full items-center truncate ${R} px-2 py-1 font-mono text-[12px] leading-none ${
                  c.isRead ? 'bg-blue-50 font-semibold text-[#3366FF]' : 'bg-blue-50 font-bold text-[#0f172a] ring-1 ring-inset ring-[#C9D9FB]'
                }`}
              >
                {c.interactionId}
              </span>
            ) : (
              <Blank />
            )}
            <RelationBadges row={c} onOpen={onOpen} />
            {(c.approvedCount ?? 0) > 0 && (
              <span className="inline-flex shrink-0 items-center gap-0.5 rounded-[3px] bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-emerald-700" title={`${c.approvedCount} approved`}>
                <CheckCircle2 className="h-3 w-3 text-emerald-500" />{c.approvedCount}
              </span>
            )}
            {(c.rejectedCount ?? 0) > 0 && (
              <span className="inline-flex shrink-0 items-center gap-0.5 rounded-[3px] bg-red-50 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-red-700" title={`${c.rejectedCount} rejected`}>
                <XCircle className="h-3 w-3 text-red-500" />{c.rejectedCount}
              </span>
            )}
            {(() => {
              const total = c.totalApprovalRequests ?? c.reviewedCount ?? 0
              const pending = Math.max(0, total - (c.reviewedCount ?? 0))
              return pending > 0 ? (
                <span className="inline-flex shrink-0 items-center gap-0.5 rounded-[3px] bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-amber-700" title={`${pending} pending`}>
                  <Clock className="h-3 w-3 text-amber-500" />{pending}
                </span>
              ) : null
            })()}
          </span>
        )
      }
      case 'customer':
        return <CustomerCell row={c} />
      case 'subject':
        return (
          <span className="block truncate" title={c.preview ? `${c.subject} — ${c.preview}` : c.subject}>
            <span className={c.isRead ? 'text-slate-700' : 'font-semibold text-slate-900'}>{c.subject}</span>
            {/* Both badges can sit together: a ticket that arrived new and has
                since been replied to is still the new ticket in the list. */}
            {markOf(c.id)?.isNew === true && (
              <span className="pill-just-updated ml-1.5 inline-flex shrink-0 items-center gap-0.5 rounded-full bg-amber-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow-sm">
                <Sparkles className="h-2.5 w-2.5" />
                New
              </span>
            )}
            {markOf(c.id)?.hasReply === true && (
              <span className="pill-just-updated ml-1.5 inline-flex shrink-0 items-center gap-0.5 rounded-full bg-[#3366FF] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow-sm">
                <Reply className="h-2.5 w-2.5" />
                Reply
              </span>
            )}
            {c.preview && <span className="text-slate-400"> — {c.preview}</span>}
          </span>
        )
      case 'age': {
        // Ageing is how long the conversation has existed, so it counts from when it
        // was created — NOT from the last message. Using last activity would
        // reset the age on every reply, which hides exactly the old conversations
        // this column exists to surface. Falls back to `timestamp` only when a
        // row predates `openedAt` being returned.
        const agedFrom = c.createdAt ?? c.timestamp
        return (
          <span className="flex min-w-0 items-center gap-1.5 text-slate-500" title={formatDateTime(agedFrom)}>
            <Clock className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            <span className="truncate">{timeAgo(agedFrom)}</span>
          </span>
        )
      }
      case 'type':
        return <IconText icon={Tag} value={c.typeLabel} />
      case 'status':
        return <StatusCell conversation={c} />
      case 'state':
        return c.stateLabel ? <Pill tone="indigo">{c.stateLabel}</Pill> : <Blank />
      case 'priority': {
        if (!c.priority) return <Blank />
        const p = PRIORITY_PILL[c.priority] ?? { tone: 'gray' as Tone, label: String(c.priority) }
        return <Pill tone={p.tone}>{p.label}</Pill>
      }
      case 'urgency':
        return c.urgencyLabel ? <Pill tone={urgencyTone(c.urgencyLabel)}>{c.urgencyLabel}</Pill> : <Blank />
      case 'category': return <IconText icon={Layers} value={c.categoryLabel} />
      case 'subcategory': return <IconText icon={FolderTree} value={c.subcategoryLabel} />
      case 'location': return <IconText icon={MapPin} value={c.locationLabel} />
      case 'branch': return <IconText icon={Building2} value={c.branchLabel} />
      case 'department': return <IconText icon={Building} value={c.departmentLabel} />
      case 'campaign': return <IconText icon={Megaphone} value={c.campaignLabel} />
      case 'team': return <IconText icon={Users} value={c.teamLabel} />
      case 'agent': return <IconText icon={Headset} value={c.assignedTo} />
      case 'slaResponse': return <SlaCell due={c.firstResponseDueAt} completedAt={c.firstRespondedAt} icon={Timer} />
      case 'slaResolution': return <SlaCell due={c.resolutionDueAt} completedAt={c.resolvedAt} icon={TimerReset} />
      case 'createdAt':
        return <IconText icon={Calendar} value={c.createdAt ? formatDateTime(c.createdAt) : undefined} className="tabular-nums text-slate-500" />
      case 'modifiedAt':
        return <IconText icon={CalendarClock} value={c.lastModifiedAt ? formatDateTime(c.lastModifiedAt) : undefined} className="tabular-nums text-slate-500" />
      // No actor on the last change → the platform did it (ingest, SLA timer,
      // auto-close), so it reads as "System" rather than a blank cell.
      case 'modifiedBy':
        return (
          <IconText
            icon={PenLine}
            value={c.lastModifiedBy || (c.lastModifiedAt ? 'System' : undefined)}
            className="text-slate-500"
          />
        )
      case 'more':
        return (
          <MoreMenu
            actions={{
              isRead: c.isRead,
              isStarred: c.isStarred,
              isArchived: c.isArchived,
              onOpen: () => handleOpen(c.id),
              onToggleStar: () => { onToggleStar(c.id); patchChild(c.id, { isStarred: !c.isStarred }) },
              onToggleRead: () => {
                if (c.isRead) onMarkUnread([c.id]); else onMarkRead([c.id])
                patchChild(c.id, { isRead: !c.isRead })
              },
              onToggleArchive: () => {
                if (c.isArchived) onUnarchive([c.id]); else onArchive([c.id])
                patchChild(c.id, { isArchived: !c.isArchived })
              },
            }}
          />
        )
      default:
        return <Blank />
    }
  }

  return (
    <table className="w-full min-w-max border-separate border-spacing-0 text-[13px]" style={{ tableLayout: 'fixed' }}>
      <thead>
        <tr>
          {cols.map((col) => (
            <th
              key={col.key}
              style={{ width: col.width, minWidth: col.width, ...(col.stickyLeft !== undefined ? { left: col.stickyLeft } : {}) }}
              className={`relative sticky top-0 h-11 border-b border-slate-200 bg-[#FAFBFC] px-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-500 ${
                col.align === 'center' ? 'text-center' : 'text-left'
              } ${
                col.stickyLeft !== undefined ? 'z-30' : 'z-20'
              } ${col.key === 'id' ? 'shadow-[1px_0_0_0_rgb(229,231,235)]' : ''}`}
            >
              {col.key === 'select' ? (
                <span className="inline-flex items-center justify-center">
                  <Checkbox
                    checked={allOnPageSelected}
                    indeterminate={someOnPageSelected}
                    onToggle={onToggleSelectAll}
                    ariaLabel="Select all conversations on this page"
                  />
                </span>
              ) : col.icon ? (
                <span className="inline-flex items-center gap-1.5">
                  <col.icon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                  <span className="truncate">{col.label}</span>
                </span>
              ) : (
                col.label
              )}
              {/* Sticky/locked columns (select, star, id) keep a fixed width —
                  resizing them would recalculate every later stickyLeft offset. */}
              {col.stickyLeft === undefined && (
                <ResizeHandle
                  startWidth={col.width}
                  onResize={(w) => handleResize(col.key, w)}
                  onResizeEnd={(w) => handleResizeEnd(col.key, w)}
                />
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {visibleRows.map((c, rowIndex) => {
          const unread = !c.isRead
          const mark = markOf(c.id)
          const justUpdated = mark !== undefined
          // Cell background lives on the <td>, not the <tr>: pinned cells sit
          // above the scrolling ones and would be see-through otherwise.
          // A just-updated row wins over the unread tint — the point is to show
          // where the list moved, which is easy to miss when it re-sorts. The
          // tint is a flat, deliberately strong colour that holds until the row
          // is opened; it used to decay over 8s, which meant a reply that landed
          // while the agent was elsewhere had already faded out by the time they
          // looked. A row carrying both badges keeps the "new" amber — it is
          // still the newly arrived ticket, now with an answer on it.
          const cellBg = mark
            ? mark.isNew
              ? 'bg-[#FDE9AC] group-hover:bg-[#FCE192]'
              : 'bg-[#CEDFFF] group-hover:bg-[#BCD2FF]'
            : unread
              ? 'bg-[#FCFDFF] group-hover:bg-[#F1F6FF]'
              : 'bg-white group-hover:bg-[#F7F9FC]'
          // Leading accent bar, on whichever cell actually renders first (the
          // select column is hidden for agents who can't act on a selection).
          const flagAccent = mark
            ? mark.isNew ? 'shadow-[inset_3px_0_0_0_#F59E0B]' : 'shadow-[inset_3px_0_0_0_#3366FF]'
            : ''
          const expandedChild = expandedParents.has(c.id)
          const expandedSecondary = expandedPrimaries.has(c.id)
          const expandedPrimary = expandedSecondaries.has(c.id)
          const childState = childrenByParent[c.id]
          const secondaryState = secondariesByPrimary[c.id]
          const primaryState = primariesBySecondary[c.id]
          
          return (
            <Fragment key={c.id}>
              <tr
                // Tour anchor. Only the first row carries it, and only when there
                // is a row at all — the guide skips steps whose target is absent,
                // so an empty inbox simply never shows the "open a conversation"
                // step instead of pointing at nothing.
                {...(rowIndex === 0 ? { id: 'tour-first-row' } : {})}
                onClick={() => handleOpen(c.id)}
                className="group cursor-pointer"
              >
                {cols.map((col, colIndex) => (
                  <td
                    key={col.key}
                    style={{ width: col.width, minWidth: col.width, maxWidth: col.width, ...(col.stickyLeft !== undefined ? { left: col.stickyLeft } : {}) }}
                    className={`h-[54px] ${expandedChild || expandedSecondary || expandedPrimary ? 'border-b border-violet-100' : 'border-b border-slate-100'} px-4 align-middle text-slate-700 transition-colors ${cellBg} ${
                      col.stickyLeft !== undefined ? 'sticky z-10' : ''
                    } ${col.align === 'center' ? 'text-center' : ''} ${
                      col.key === 'id' ? 'shadow-[1px_0_0_0_rgb(243,244,246)]' : ''
                    } ${colIndex === 0 && justUpdated ? flagAccent : ''} ${
                      colIndex === 0 && (expandedChild || expandedSecondary || expandedPrimary) && !justUpdated ? 'shadow-[inset_3px_0_0_0_#8B5CF6]' : ''
                    } ${
                      col.key === 'star' && unread && !justUpdated ? 'shadow-[inset_3px_0_0_0_#3366FF]' : ''
                    }`}
                  >
                    <div className={col.align === 'center' ? 'flex items-center justify-center' : 'truncate'}>
                      {cellFor(c, col.key)}
                    </div>
                  </td>
                ))}
              </tr>
              {/* Child ticket error/loading/empty/rows */}
              {expandedChild && childState?.error && (
                <tr className="bg-violet-50/40">
                  <td colSpan={cols.length} className="border-b border-violet-100 px-4 py-2 text-[12px] text-red-600 shadow-[inset_3px_0_0_0_#8B5CF6]">
                    <span className="sticky left-4 inline-flex items-center gap-2">
                      Could not load child tickets.
                      <button type="button" onClick={(e) => { e.stopPropagation(); loadChildren(c.id) }} className="font-semibold underline">Retry</button>
                    </span>
                  </td>
                </tr>
              )}
              {expandedChild && childState?.loading && !childState.items && (
                <tr className="bg-violet-50/40">
                  <td colSpan={cols.length} className="border-b border-violet-100 px-4 py-2 text-[12px] text-slate-400 shadow-[inset_3px_0_0_0_#8B5CF6]">
                    <span className="sticky left-4 inline-flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" />Loading child tickets…</span>
                  </td>
                </tr>
              )}
              {expandedChild && childState?.items?.length === 0 && !childState.loading && !childState.error && (
                <tr className="bg-violet-50/40">
                  <td colSpan={cols.length} className="border-b border-violet-100 px-4 py-2 text-[12px] text-slate-400 shadow-[inset_3px_0_0_0_#8B5CF6]">
                    <span className="sticky left-4">No child tickets found.</span>
                  </td>
                </tr>
              )}
              {expandedChild && childState?.items?.map((child, childIndex) => {
                const last = childIndex === (childState.items?.length ?? 0) - 1
                const childBg = selectedIds.has(child.id)
                  ? 'bg-[#EEF3FF] group-hover:bg-[#E3ECFF]'
                  : 'bg-[#FAF8FF] group-hover:bg-[#F3EFFF]'
                return (
                  <tr key={child.id} onClick={() => handleOpen(child.id)} className="group cursor-pointer">
                    {cols.map((col, colIndex) => (
                      <td
                        key={col.key}
                        style={{ width: col.width, minWidth: col.width, maxWidth: col.width, ...(col.stickyLeft !== undefined ? { left: col.stickyLeft } : {}) }}
                        className={`h-[54px] border-b ${last ? 'border-violet-200' : 'border-violet-100/70'} px-4 align-middle text-slate-700 transition-colors ${childBg} ${
                          col.stickyLeft !== undefined ? 'sticky z-10' : ''
                        } ${col.align === 'center' ? 'text-center' : ''} ${
                          col.key === 'id' ? 'py-0 shadow-[1px_0_0_0_rgb(237,233,254)]' : ''
                        } ${colIndex === 0 ? 'shadow-[inset_3px_0_0_0_#C4B5FD]' : ''}`}
                      >
                        <div className={col.align === 'center' ? 'flex items-center justify-center' : 'truncate'}>
                          {cellFor(child, col.key, last ? 'last' : 'mid')}
                        </div>
                      </td>
                    ))}
                  </tr>
                )
              })}
              
              {/* Secondary ticket error/loading/empty/rows */}
              {expandedSecondary && secondaryState?.error && (
                <tr className="bg-indigo-50/40">
                  <td colSpan={cols.length} className="border-b border-indigo-100 px-4 py-2 text-[12px] text-red-600 shadow-[inset_3px_0_0_0_#818CF8]">
                    <span className="sticky left-4 inline-flex items-center gap-2">
                      Could not load secondary tickets.
                      <button type="button" onClick={(e) => { e.stopPropagation(); loadSecondaries(c.id) }} className="font-semibold underline">Retry</button>
                    </span>
                  </td>
                </tr>
              )}
              {expandedSecondary && secondaryState?.loading && !secondaryState.items && (
                <tr className="bg-indigo-50/40">
                  <td colSpan={cols.length} className="border-b border-indigo-100 px-4 py-2 text-[12px] text-slate-400 shadow-[inset_3px_0_0_0_#818CF8]">
                    <span className="sticky left-4 inline-flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" />Loading secondary tickets…</span>
                  </td>
                </tr>
              )}
              {expandedSecondary && secondaryState?.items?.length === 0 && !secondaryState.loading && !secondaryState.error && (
                <tr className="bg-indigo-50/40">
                  <td colSpan={cols.length} className="border-b border-indigo-100 px-4 py-2 text-[12px] text-slate-400 shadow-[inset_3px_0_0_0_#818CF8]">
                    <span className="sticky left-4">No secondary tickets found.</span>
                  </td>
                </tr>
              )}
              {expandedSecondary && secondaryState?.items?.map((sec, secIndex) => {
                const last = secIndex === (secondaryState.items?.length ?? 0) - 1
                const secBg = selectedIds.has(sec.id)
                  ? 'bg-[#EEF3FF] group-hover:bg-[#E3ECFF]'
                  : 'bg-indigo-50/30 group-hover:bg-indigo-50/50'
                return (
                  <tr key={sec.id} onClick={() => handleOpen(sec.id)} className="group cursor-pointer">
                    {cols.map((col, colIndex) => (
                      <td
                        key={col.key}
                        style={{ width: col.width, minWidth: col.width, maxWidth: col.width, ...(col.stickyLeft !== undefined ? { left: col.stickyLeft } : {}) }}
                        className={`h-[54px] border-b ${last ? 'border-indigo-200' : 'border-indigo-100/70'} px-4 align-middle text-slate-700 transition-colors ${secBg} ${
                          col.stickyLeft !== undefined ? 'sticky z-10' : ''
                        } ${col.align === 'center' ? 'text-center' : ''} ${
                          col.key === 'id' ? 'py-0 shadow-[1px_0_0_0_rgb(224,231,255)]' : ''
                        } ${colIndex === 0 ? 'shadow-[inset_3px_0_0_0_#A5B4FC]' : ''}`}
                      >
                        <div className={col.align === 'center' ? 'flex items-center justify-center' : 'truncate'}>
                          {cellFor(sec, col.key, last ? 'last' : 'mid')}
                        </div>
                      </td>
                    ))}
                  </tr>
                )
              })}
              
              {/* Primary ticket error/loading/empty/rows */}
              {expandedPrimary && primaryState?.error && (
                <tr className="bg-slate-50/40">
                  <td colSpan={cols.length} className="border-b border-slate-100 px-4 py-2 text-[12px] text-red-600 shadow-[inset_3px_0_0_0_#94A3B8]">
                    <span className="sticky left-4 inline-flex items-center gap-2">
                      Could not load primary ticket.
                      <button type="button" onClick={(e) => { e.stopPropagation(); loadPrimary(c.id, c.mergedIntoId!) }} className="font-semibold underline">Retry</button>
                    </span>
                  </td>
                </tr>
              )}
              {expandedPrimary && primaryState?.loading && !primaryState.items && (
                <tr className="bg-slate-50/40">
                  <td colSpan={cols.length} className="border-b border-slate-100 px-4 py-2 text-[12px] text-slate-400 shadow-[inset_3px_0_0_0_#94A3B8]">
                    <span className="sticky left-4 inline-flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" />Loading primary ticket…</span>
                  </td>
                </tr>
              )}
              {expandedPrimary && primaryState?.items?.length === 0 && !primaryState.loading && !primaryState.error && (
                <tr className="bg-slate-50/40">
                  <td colSpan={cols.length} className="border-b border-slate-100 px-4 py-2 text-[12px] text-slate-400 shadow-[inset_3px_0_0_0_#94A3B8]">
                    <span className="sticky left-4">No primary ticket found.</span>
                  </td>
                </tr>
              )}
              {expandedPrimary && primaryState?.items?.map((prim) => {
                const primBg = selectedIds.has(prim.id)
                  ? 'bg-[#EEF3FF] group-hover:bg-[#E3ECFF]'
                  : 'bg-slate-50/30 group-hover:bg-slate-50/50'
                return (
                  <tr key={prim.id} onClick={() => handleOpen(prim.id)} className="group cursor-pointer">
                    {cols.map((col, colIndex) => (
                      <td
                        key={col.key}
                        style={{ width: col.width, minWidth: col.width, maxWidth: col.width, ...(col.stickyLeft !== undefined ? { left: col.stickyLeft } : {}) }}
                        className={`h-[54px] border-b border-slate-200 px-4 align-middle text-slate-700 transition-colors ${primBg} ${
                          col.stickyLeft !== undefined ? 'sticky z-10' : ''
                        } ${col.align === 'center' ? 'text-center' : ''} ${
                          col.key === 'id' ? 'py-0 shadow-[1px_0_0_0_rgb(241,245,249)]' : ''
                        } ${colIndex === 0 ? 'shadow-[inset_3px_0_0_0_#CBD5E1]' : ''}`}
                      >
                        <div className={col.align === 'center' ? 'flex items-center justify-center' : 'truncate'}>
                          {cellFor(prim, col.key, 'last')}
                        </div>
                      </td>
                    ))}
                  </tr>
                )
              })}
            </Fragment>
          )
        })}
      </tbody>
    </table>
  )
}
